import Stomp from 'stompjs';
import SockJS from 'sockjs-client';
import { toast } from 'react-toastify';

let client = null;
let connected = false;
let subscriptions = new Map(); // to track active subscriptions
let reconnectTimer = null;

export const stompService = {
  /**
   * Connects to the STOMP WebSocket endpoint
   * @param {string} url - WebSocket endpoint (e.g., `${API_URI}/ws`)
   * @param {function} onConnect - callback on success
   * @param {function} onError - callback on failure
   */
  connect: (url, onConnect, onError) => {
    // Prevent duplicate connection
    if (connected && client?.connected) return client;

    const socket = new SockJS(url);
    client = Stomp.over(socket);

    // Optional: remove debug logs in production
    client.debug = null;

    client.connect(
      {},
      (frame) => {
        connected = true;
        console.log("✅ STOMP connected:", frame?.headers);

        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }

        if (onConnect) onConnect(frame);

        // Re-subscribe to topics if any were lost on reconnect
        subscriptions.forEach((value, topic) => {
          client.subscribe(topic, value);
        });
      },
      (error) => {
        connected = false;
        console.error("❌ STOMP connection failed:", error);

        if (onError) onError(error);

        // Reconnect automatically after delay (5s)
        if (!reconnectTimer) {
          reconnectTimer = setTimeout(() => {
            console.log("♻️ Attempting STOMP reconnect...");
            stompService.connect(url, onConnect, onError);
          }, 5000);
        }
      }
    );

    return client;
  },

  /**
   * Subscribe to a topic and return unsubscribe function
   * @param {string} topic - e.g. `/topic/connect/${userId}`
   * @param {function} callback - called with message body
   * @returns {function} unsubscribe - call to cancel subscription
   */
  subscribe: (topic, callback) => {
    if (!client || !connected) {
      console.warn("⚠️ STOMP client not connected yet — subscription deferred.");
      return null;
    }

    const subscription = client.subscribe(topic, (message) => {
      try {
        const parsed = JSON.parse(message.body);
        callback(parsed, message);
      } catch {
        callback(message.body, message);
      }
    });

    // Store the subscription for possible re-subscription on reconnect
    subscriptions.set(topic, (message) => {
      try {
        const parsed = JSON.parse(message.body);
        callback(parsed, message);
      } catch {
        callback(message.body, message);
      }
    });

    // Return unsubscribe handler
    return () => {
      if (subscription) {
        subscription.unsubscribe();
        subscriptions.delete(topic);
        console.log(`🧹 Unsubscribed from ${topic}`);
      }
    };
  },

  /**
   * Send message to endpoint
   * @param {string} destination - e.g. `/app/upload`
   * @param {object|string} body
   */
  send: (destination, body = {}) => {
    if (!client || !connected) {
      console.warn("⚠️ Cannot send — STOMP client not connected.");
      return;
    }
    const payload = typeof body === "string" ? body : JSON.stringify(body);
    client.send(destination, {}, payload);
  },

  /**
   * Disconnect from STOMP (and clear all subscriptions)
   */
  disconnect: () => {
    if (client && connected) {
      client.disconnect(() => {
        connected = false;
        console.log("🔌 STOMP disconnected");
        subscriptions.clear();
      });
    }
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  },

  /**
   * Check connection state
   */
  isConnected: () => connected,
};
